import { DropDownOption } from "@/types/dropdown";
import { Co2MaterialSettingType, MetadataEntityType, Scenario, ScenarioMetadata, UpdateScenarioRequest } from "@api/apiClient";
import { useAppSettings } from "@api/appSettings";
import RebarEstimationPage from "@features/settings/components/RebarEstimationPage/RebarEstimationPage";
import { useAdditionalMetadataFeature } from "@hooks/useAdditionalMetadataFeature";
import useAppStore from "@hooks/useAppStore";
import i18n from "@i18n/i18n";
import CloseIcon from "@mui/icons-material/Close";
import { Alert, Box, Button, Dialog, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select, Snackbar } from "@mui/material";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { ScenarioMetadataForm } from "./ScenarioMetadataForm";

export interface ScenarioSettingsDialogProps {
  open: boolean;
  scenario: Scenario | null;
  onClose: () => void;
  onSave: (scenario: any) => void;
}

const ScenarioSettingsDialog = observer((props: ScenarioSettingsDialogProps) => {
  const appStore = useAppStore();
  const { open, scenario, onClose, onSave } = props;
  const { isEnabled: showMetadata } = useAdditionalMetadataFeature();
  const appSettings = useAppSettings();
  const [selectedProjectPhaseValue, setSelectedProjectPhaseValue] = useState<string>("");
  const [selectedAssetValue, setSelectedAssetValue] = useState<string>("");
  // State for metadata form
  const [metadata, setMetadata] = useState<ScenarioMetadata>(
    new ScenarioMetadata({
      totalFloorArea: undefined,
      serviceLife: undefined,
    }),
  );

  // Track if forms have changed
  const [hasMetadataChanged, setHasMetadataChanged] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [projectPhaseOptions, setProjectPhaseOptions] = useState<DropDownOption[]>([]);
  const [assetOptions, setAssetOptions] = useState<DropDownOption[]>([]);
  useEffect(() => {
    // Filter for ProjectPhase entities and map to dropdown options
    const options: DropDownOption[] =
      appStore.applicationSettings?.metadataEntities
        ?.filter((entity) => entity.type === MetadataEntityType.ProjectPhase)
        .map((entity) => ({
          key: entity.metadataEntityId as string,
          label: entity.name as string,
        })) ?? [];
    setProjectPhaseOptions(options);
    setSelectedProjectPhaseValue(scenario?.projectPhaseId || "");
  }, [appStore.applicationSettings?.metadataEntities, props.scenario]);

  useEffect(() => {
    // Filter for Asset entities and map to dropdown options
    const options: DropDownOption[] =
      appStore.applicationSettings?.metadataEntities
        ?.filter((entity) => (entity.type as string) === "Asset")
        .map((entity) => ({
          key: entity.metadataEntityId as string,
          label: entity.name as string,
        })) ?? [];
    setAssetOptions(options);
    setSelectedAssetValue(scenario?.assetId || "");
  }, [appStore.applicationSettings?.metadataEntities, props.scenario]);

  // Load data from scenario when scenario changes
  useEffect(() => {
    if (scenario?.scenarioMetadata) {
      setMetadata(
        new ScenarioMetadata({
          totalFloorArea: scenario.scenarioMetadata.totalFloorArea,
          serviceLife: scenario.scenarioMetadata.serviceLife,
        }),
      );
      // Reset change tracking
      setHasMetadataChanged(false);
    }
  }, [scenario]);

  const handleMetadataChange = (field: string, value: number | undefined) => {
    setMetadata((prev) => {
      const newData = {
        totalFloorArea: prev.totalFloorArea,
        serviceLife: prev.serviceLife,
        [field]: value,
      };
      return new ScenarioMetadata(newData);
    });
    setHasMetadataChanged(true);
  };

  const handleSaveMetadata = async () => {
    try {
      setIsSubmitting(true);
      const updateRequest = new UpdateScenarioRequest({
        scenarioId: scenario?.scenarioId || "",
        scenarioMetadata: new ScenarioMetadata({
          totalFloorArea: metadata.totalFloorArea,
          serviceLife: metadata.serviceLife,
        }),
        projectPhaseId: selectedProjectPhaseValue || "",
        assetId: selectedAssetValue || "",
      });

      await onSave(updateRequest);

      // Reset change tracking after successful save
      setHasMetadataChanged(false);
      setShowSuccessMessage(true);
    } catch (error) {
      console.error("Error updating scenario metadata:", error);
      setErrorMessage("Failed to update scenario metadata. Please try again.");
      setShowErrorMessage(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setMetadata(
      new ScenarioMetadata({
        totalFloorArea: undefined,
        serviceLife: undefined,
      }),
    );

    setHasMetadataChanged(false);
    onClose();
  };

  const handleCloseSuccessMessage = () => {
    setShowSuccessMessage(false);
  };

  const handleCloseErrorMessage = () => {
    setShowErrorMessage(false);
  };

  const hasAnyChanges = hasMetadataChanged;

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth={"xl"}
        open={open}
        onClose={handleClose}
        aria-labelledby="scenario-settings-dialog-title"
        PaperProps={{
          sx: {
            height: "80%",
          },
        }}
      >
        <DialogTitle
          id="scenario-settings-dialog-title"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {i18n.t("scenarios-grid.settings-button.title")}
          <IconButton aria-label="close" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            paddingLeft: 2,
            paddingRight: 2,
            paddingBottom: 0,
            gap: 2,
          }}
        >
          {/* Additional Metadata Section */}
          {
            <Box>
              <FormControl fullWidth sx={{ mb: 2, mt: 2 }}>
                <InputLabel>Asset</InputLabel>
                <Select
                  value={selectedAssetValue ?? ""}
                  label="Asset"
                  onChange={(event) => {
                    const newAssetId = event.target.value;
                    setSelectedAssetValue(newAssetId);
                    const hasChanged = props.scenario?.asset?.metadataEntityId !== newAssetId;
                    setHasMetadataChanged(hasChanged);
                  }}
                >
                  {assetOptions.map((option: DropDownOption) => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{i18n.t("scenarios-tab.scenario-dialog.project-phase-input.title")}</InputLabel>
                <Select
                  value={selectedProjectPhaseValue ?? ""}
                  label={i18n.t("scenarios-tab.scenario-dialog.project-phase-input.title")}
                  onChange={(event) => {
                    const newProjectPhaseId = event.target.value;
                    setSelectedProjectPhaseValue(newProjectPhaseId);
                    const hasChanged = props.scenario?.projectPhase?.metadataEntityId !== newProjectPhaseId;
                    setHasMetadataChanged(hasChanged);
                  }}
                >
                  {projectPhaseOptions.map((option: DropDownOption) => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {showMetadata && <ScenarioMetadataForm metadata={metadata} onChange={handleMetadataChange} />}

              {/* Save button positioned after Additional Metadata */}
              {hasAnyChanges && (
                <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                  <Button variant="contained" onClick={handleSaveMetadata} disabled={isSubmitting}>
                    {isSubmitting ? i18n.t("project-settings-page.save-metadata-button.on-saving") : i18n.t("project-settings-page.save-metadata-button.title")}
                  </Button>
                </Box>
              )}
            </Box>
          }

          {/* Rebar Estimation Section -- replace the condition with the relevant rebar estimation settings */}
          {true && (
            <Box sx={{ flexGrow: 1, display: "flex" }}>
              <RebarEstimationPage
                originScreen="Scenario"
                mode={appSettings.data?.profileLevelRebarSettings ? Co2MaterialSettingType.Profile : Co2MaterialSettingType.Scenario}
                projectId={scenario?.projectId}
                scenarioId={scenario?.scenarioId}
                profileId={scenario?.profileId}
                rebarEnabled={scenario?.profile?.rebarEstimationStatus}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Success message */}
      <Snackbar open={showSuccessMessage} autoHideDuration={3000} onClose={handleCloseSuccessMessage} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={handleCloseSuccessMessage} severity="success" variant="filled">
          Scenario updated successfully!
        </Alert>
      </Snackbar>

      {/* Error message */}
      <Snackbar open={showErrorMessage} autoHideDuration={5000} onClose={handleCloseErrorMessage} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={handleCloseErrorMessage} severity="error" variant="filled">
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
});

export default ScenarioSettingsDialog;
